import { useHomeCrumb } from "@/components/PageBreadcrumbs";
import PageBackButton from "@/components/PageBackButton";
import { Breadcrumbs, Icon } from "@app-components";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useLoggedInUser } from "@/store/zustandStore";
import { useWelcomeBookingAccess } from "@/hooks/useWelcomeBookingAccess";
import { useBuilding, useUserBookings } from "@/api";
import Table from "@/components/table/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { MeetingRoomBooking, MeetingRoomBookingStatus } from "@/types";
import { useMemo } from "react";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import {
  BOOKING_DATE_LOCALE,
  dateToTimeInputString,
  NOK,
  NUMERIC_DATE_TIME_LOCALE,
} from "@/lib";
import { Tooltip } from "react-tooltip";
import BookingCostBreakdown, {
  bookingCostBreakdownText,
} from "@/components/bookings/BookingCostBreakdown";

const columnHelper = createColumnHelper<MeetingRoomBooking>();

export const Route = createFileRoute("/_portal/bookings/history")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useLoggedInUser();
  const { data: building } = useBuilding();
  const { allowWelcomeBookingAccess } = useWelcomeBookingAccess();
  const { data: bookings, isLoading } = useUserBookings(
    {
      userId: user?.userId,
      companyId: user?.buildingTenantId,
    },
    allowWelcomeBookingAccess,
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        cell: (info) => info.getValue(),
        header: () => <div>ID</div>,
        meta: {
          label: "ID",
        },
      }),
      columnHelper.accessor("status", {
        cell: (info) => {
          const status = info.getValue();
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--wlcm-spacing-xs)",
              }}
            >
              <div
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 20,
                  background:
                    status === MeetingRoomBookingStatus.CONFIRMED
                      ? "var(--wlcm-color-blue)"
                      : "var(--wlcm-color-light-black)",
                }}
              ></div>
              <span>
                {t(`BOOKINGS.HISTORY_TABLE.STATUS.${status.toUpperCase()}`)}
              </span>
            </div>
          );
        },
        header: (info) => <SortableTableHeader info={info} label="Status" />,
        meta: {
          label: "Status",
        },
      }),
      columnHelper.accessor("createdTime", {
        cell: (info) =>
          NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            new Date(info.getValue()),
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("BOOKINGS.BUILDING_TABLE.CREATED_TIME")}
          />
        ),
        meta: {
          label: t("BOOKINGS.BUILDING_TABLE.CREATED_TIME"),
        },
      }),
      columnHelper.accessor("lastEditTime", {
        cell: (info) =>
          NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            new Date(info.getValue()),
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("BOOKINGS.BUILDING_TABLE.UPDATED_TIME")}
          />
        ),
        meta: {
          label: t("BOOKINGS.BUILDING_TABLE.UPDATED_TIME"),
        },
      }),
      columnHelper.accessor(
        (row) => [row.event.start, row.event.end, row.status],
        {
          id: "dateTime",
          cell: (info) => {
            const [start, end, status] = info.getValue();
            if (!start || !end) return "";
            return (
              <span
                style={{
                  textTransform: "capitalize",
                  textDecoration:
                    status === MeetingRoomBookingStatus.CANCELLED
                      ? "line-through"
                      : "none",
                }}
              >
                {BOOKING_DATE_LOCALE(i18n.language).format(new Date(start))},{" "}
                {dateToTimeInputString(new Date(start))} -{" "}
                {dateToTimeInputString(new Date(end))}
              </span>
            );
          },
          header: (info) => (
            <SortableTableHeader
              info={info}
              label={`${t("BOOKINGS.FILTERS.DATE")} & ${t("BOOKINGS.MODAL.LABELS.TIME")}`}
            />
          ),
          sortingFn: (a, b) => {
            const [startA, startB] = [a, b].map(
              (x) => new Date(x.original.event.start as Date),
            );
            return startA.getTime() - startB.getTime();
          },
          meta: {
            label: `${t("BOOKINGS.FILTERS.DATE")} & ${t("BOOKINGS.MODAL.LABELS.TIME")}`,
          },
        },
      ),
      columnHelper.accessor("roomName", {
        cell: (info) => info.getValue(),
        header: () => <div>{t("BOOKINGS.MODAL.LABELS.ROOM")}</div>,
        meta: {
          label: t("BOOKINGS.MODAL.LABELS.ROOM"),
        },
      }),
      columnHelper.accessor("event.title", {
        id: "eventTitle",
        cell: (info) => info.getValue(),
        header: () => <div>{t("BOOKINGS.MODAL.LABELS.MEETING_TITLE")}</div>,
        meta: {
          label: t("BOOKINGS.MODAL.LABELS.MEETING_TITLE"),
        },
      }),
      ...(building?.bookingSettings?.showNotesInput
        ? [
            columnHelper.accessor("notes", {
              cell: (info) => {
                const note = info.getValue();
                return note ? (
                  <>
                    <span className="sr-only">"{note}"</span>
                    <Icon
                      name="comment"
                      className={`comment-${info.row.original.id}`}
                      style={{
                        margin: "0 var(--wlcm-spacing-xs)",
                        verticalAlign: "middle",
                      }}
                    />
                    <Tooltip
                      anchorSelect={`.comment-${info.row.original.id}`}
                      content={`"${note}"`}
                    />
                  </>
                ) : (
                  <></>
                );
              },
              header: () => (
                <div className="sr-only">
                  {t("BOOKINGS.MODAL.LABELS.NOTES")}
                </div>
              ),
              meta: {
                label: t("BOOKINGS.MODAL.LABELS.NOTES"),
              },
            }),
          ]
        : []),
      columnHelper.accessor(
        (row) => [
          row.id,
          row.fixedBookingFee,
          row.totalPrice,
          row.hourlyPrice,
          row.maxPricePerBooking,
          row.companyDiscount,
          row.duration,
          row.status,
        ],
        {
          id: "price",
          cell: (info) => {
            const [
              bookingId,
              fixedFee,
              totalPrice,
              hourlyRate,
              maxPricePerBooking,
              companyDiscount,
              duration,
              status,
            ] = info.getValue() as [
              string,
              number,
              number,
              number,
              number | undefined,
              number,
              number,
              MeetingRoomBookingStatus,
            ];
            const hasCostBreakdownData =
              fixedFee != null &&
              hourlyRate != null &&
              maxPricePerBooking != null &&
              companyDiscount != null &&
              duration != null;
            return (
              <>
                {totalPrice == null ? (
                  <span>{t("UNKNOWN")}</span>
                ) : (
                  <span
                    style={
                      status === MeetingRoomBookingStatus.CANCELLED
                        ? { textDecoration: "line-through" }
                        : {}
                    }
                  >
                    {NOK.format(totalPrice)}
                  </span>
                )}
                {hasCostBreakdownData && (
                  <>
                    <span className="sr-only">
                      {" "}
                      {bookingCostBreakdownText(
                        {
                          fixedFee,
                          hourlyRate,
                          maxPricePerBooking,
                          duration,
                          companyDiscountMultiplier: companyDiscount,
                        },
                        t,
                      )}
                    </span>
                    <Icon
                      name="help"
                      className={`_${bookingId}-price-calculation`}
                      style={{
                        marginLeft: "var(--wlcm-spacing-xs)",
                        opacity: 0.5,
                        verticalAlign: "middle",
                      }}
                    />
                    <Tooltip
                      anchorSelect={`._${bookingId}-price-calculation`}
                      children={
                        <BookingCostBreakdown
                          fixedFee={fixedFee}
                          hourlyRate={hourlyRate}
                          maxPricePerBooking={maxPricePerBooking}
                          duration={duration}
                          companyDiscountMultiplier={companyDiscount}
                        />
                      }
                    />
                  </>
                )}
              </>
            );
          },
          header: (info) => (
            <SortableTableHeader
              info={info}
              label={t("BOOKINGS.BUILDING_TABLE.TOTAL_COST")}
            />
          ),
          sortingFn: (a, b) =>
            (a.original.totalPrice ?? 0) - (b.original.totalPrice ?? 0),
          meta: {
            label: t("BOOKINGS.BUILDING_TABLE.TOTAL_COST"),
          },
        },
      ),
      columnHelper.accessor("invoiceReference", {
        cell: (info) => {
          const rawVal = info.getValue();
          const normalizedVal: string = !rawVal
            ? "-"
            : typeof rawVal === "string"
              ? rawVal
              : Object.values(rawVal).join(", ");
          return <span style={{ fontStyle: "italic" }}>{normalizedVal}</span>;
        },
        header: () => <div>{t("BOOKINGS.MODAL.LABELS.INVOICE_REFERENCE")}</div>,
        meta: {
          label: t("BOOKINGS.MODAL.LABELS.INVOICE_REFERENCE"),
        },
      }),
    ],
    [building, i18n.language, t],
  );

  const homeCrumb = useHomeCrumb();
  const breadcrumbs = [
    ...homeCrumb,
    { label: t("PAGES.BOOKING.HEADER"), path: "/bookings" },
    {
      label: t("ACCESS_CARD.DETAILS_MODAL.HISTORY"),
      path: `/bookings/history`,
    },
  ];

  if (!allowWelcomeBookingAccess) return null;

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: "/bookings" }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("PAGES.BOOKING.HISTORY.HEADER")}</h1>
        <p>{t("PAGES.BOOKING.HISTORY.SUBHEADER")}</p>
      </div>
      <Table
        name="bookingHistory"
        columns={columns}
        data={bookings?.previous}
        isLoading={isLoading}
        noDataMessage={t("BOOKINGS.BUILDING_TABLE.NO_BOOKINGS")}
        className="absoluteMaxWidth"
        defaultSorting={[
          {
            id: "dateTime",
            desc: true,
          },
        ]}
        defaultColumnVisibility={{
          id: false,
          createdTime: false,
          lastEditTime: false,
          invoiceReference: false,
        }}
        enableColumnControls
      />
    </>
  );
}
